import React, { useEffect, useRef, useState } from "react";
import useAxiosProtect from "../../../../hooks/useAxiosProtect";
import useAuth from "../../../../hooks/useAuth";
import {TableHead, TableRow, Table, TableBody, TableCell, TableContainer} from '@mui/material';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import IconButton from '@mui/material/IconButton';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import AccountManagementModal from "./AccountManagementModal";
import CreateOppModal from '../../../opportunities/CreateOppModal';
import DeleteOppModal from '../../../opportunities/DeleteOppModal';
import StatusModal from '../../../opportunities/StatusModal';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import PaybackModal from '../../../opportunities/PaybackModal';
import { useMemo } from "react";

const UserAccountPage = props => {
    const axiosProtect = useAxiosProtect(); 
    const {auth} = useAuth();

    const [rows, setRows] = useState([]);
    const [users, setUsers] = useState([]);
    const [userToEdit, setUserToEdit] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [openSortTooltip, setOpenSortTooltip] = useState(false);
    const [sortState, setSortState] = useState('name-asc');
    const field = sortState.field;
    const dir = sortState.direction;
    const [queryString, setQueryString] = useState('');    

    const [modalState, setModalState] = useState('');
    const [createModalState, setCreateModalState] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [paybackModalOpen, setPaybackModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const [oppToDelete, setOppToDelete] = useState(null);
    const [oppToEdit, setOppToEdit] = useState(null);

    const [selectedAccess, setSelectedAccess] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState('');

    var init = useRef(true);

    const handleEditClick = user => {
        setUserToEdit(user);
    }

    const handleDeleteClick = user => {
        setUserToDelete(user);
    }

    const closeModal = () => {
        setUserToEdit(null);
        setUserToDelete(null);
        setModalState('');
    }

    useEffect(() => {
        if(!init.current) return;
        init.current = false;
        
        let siteID = isNaN(parseInt(props.siteID)) ? window.location.href.split('/')[window.location.href.split('/').length - 1] : props.siteID;

        axiosProtect.get(`/auth/query/get-user-list?uid=${auth.id}`).then(response => {
            setUsers(response.data.users)
        }).catch(err => {
            console.log("Could not get opps");
            console.log(err);

            if (err.response) {
                console.log("Response data:", err.response.data);
                console.log("Status:", err.response.status);
            } else if (err.request) {
                console.log("No response received:", err.request);
            } else {
                console.log("Error message:", err.message);
            }
        });
    }, []);
    

    useEffect(() => {
        if(oppToEdit == null) return;
        setCreateModalState('edit');
    }, [oppToEdit]);

    useEffect(() => {
        if(createModalState === '') setOppToEdit(null);
    }, [createModalState]);

    const createData = (id, name, category, savings, ghg, cost, payback, progress, start, end, manager, status, waterData, elecData, gasData, low_savings_alert, manager_id, organization) => {
        let payload = {
            id,
            name,
            category,
            savings,
            ghg,
            cost,
            payback,
            progress,
            start,
            end,
            // manager,
            status,
            waterData,
            elecData,
            gasData,
            low_savings_alert,
            manager_id, 
            organization, 
        }
        if(parseInt(auth.role) >= parseInt(process.env.REACT_APP_ES_USER)) payload.manager = manager;
            return payload;
    }

    const addOpportunity = opp => {
        let opps = [...rows];

        opps.push(createData(
            opp.id, 
            opp.title,
            opp.category.charAt(0).toUpperCase() + opp.category.slice(1),
            (opp.water_savings_m3_year * opp.water_unit_cost_m3) + (opp.electricity_savings_kwh_year * opp.electricity_unit_cost_kwh) + (opp.gas_savings_units_year * opp.gas_unit_cost),
            (opp.water_savings_m3_year * opp.water_kg_co2_m3) + (opp.electricity_savings_kwh_year * opp.electricity_kg_co2_kwh) + (opp.gas_savings_units_year * opp.gas_kg_co2_btu * opp.gas_btu_per_unit),
            opp.project_cost,
            opp.payback_years,
            opp.project_progress_percent,
            opp.start_date ? opp.start_date : 'TBD',
            opp.end_date ? opp.end_date : 'TBD',
            `${opp.first_name.substring(0,1)}. ${opp.last_name}`,
            opp.project_progress_status.charAt(0).toUpperCase() + opp.project_progress_status.slice(1),
            {
            included: opp.has_water,
            utilitySavings: opp.water_savings_m3_year,
            costSavings: opp.water_unit_cost_m3 * opp.water_savings_m3_year,
            ghgSavings: opp.water_kg_co2_m3 * opp.water_savings_m3_year,
            ghgFactor: opp.water_kg_co2_m3,
            unitCost: opp.water_unit_cost_m3
            },
            {
            included: opp.has_electricity,
            utilitySavings: opp.electricity_savings_kwh_year,
            costSavings: opp.electricity_unit_cost_kwh * opp.electricity_savings_kwh_year,
            ghgSavings: opp.electricity_kg_co2_kwh * opp.electricity_savings_kwh_year,
            ghgFactor: opp.electricity_kg_co2_kwh,
            unitCost: opp.electricity_unit_cost_kwh
            },
            {
            included: opp.has_gas,
            utilitySavings: opp.gas_savings_units_year,
            costSavings: opp.gas_unit_cost * opp.gas_savings_units_year,
            ghgSavings: opp.gas_kg_co2_btu * opp.gas_savings_units_year * opp.gas_btu_per_unit,
            gasType: opp.gas_type,
            ghgFactor: opp.gas_kg_co2_btu,
            unitCost: opp.gas_unit_cost
            },
            opp.low_savings_alert,
            opp.project_lead_user_id, 
            opp.organization_name, 
        ));
        setRows(opps);
    }


    const updateOpportunity = opp => {
        let opps = [...rows];

        for(let i = 0;i<opps.length;++i){
            if(opps[i].id == opp.id){
            opps[i] = createData(
                opp.id, 
                opp.title,
                opp.category.charAt(0).toUpperCase() + opp.category.slice(1),
                (opp.water_savings_m3_year * opp.water_unit_cost_m3) + (opp.electricity_savings_kwh_year * opp.electricity_unit_cost_kwh) + (opp.gas_savings_units_year * opp.gas_unit_cost),
                (opp.water_savings_m3_year * opp.water_kg_co2_m3) + (opp.electricity_savings_kwh_year * opp.electricity_kg_co2_kwh) + (opp.gas_savings_units_year * opp.gas_kg_co2_btu * opp.gas_btu_per_unit),
                opp.project_cost,
                opp.payback_years,
                opp.project_progress_percent,
                opp.start_date ? opp.start_date : 'TBD',
                opp.end_date ? opp.end_date : 'TBD',
                `${opp.first_name.substring(0,1)}. ${opp.last_name}`,
                opp.project_progress_status.charAt(0).toUpperCase() + opp.project_progress_status.slice(1),
                {
                included: opp.has_water,
                utilitySavings: opp.water_savings_m3_year,
                costSavings: opp.water_unit_cost_m3 * opp.water_savings_m3_year,
                ghgSavings: opp.water_kg_co2_m3 * opp.water_savings_m3_year,
                ghgFactor: opp.water_kg_co2_m3,
                unitCost: opp.water_unit_cost_m3
                },
                {
                included: opp.has_electricity,
                utilitySavings: opp.electricity_savings_kwh_year,
                costSavings: opp.electricity_unit_cost_kwh * opp.electricity_savings_kwh_year,
                ghgSavings: opp.electricity_kg_co2_kwh * opp.electricity_savings_kwh_year,
                ghgFactor: opp.electricity_kg_co2_kwh,
                unitCost: opp.electricity_unit_cost_kwh
                },
                {
                included: opp.has_gas,
                utilitySavings: opp.gas_savings_units_year,
                costSavings: opp.gas_unit_cost * opp.gas_savings_units_year,
                ghgSavings: opp.gas_kg_co2_btu * opp.gas_savings_units_year * opp.gas_btu_per_unit,
                gasType: opp.gas_type,
                ghgFactor: opp.gas_kg_co2_btu,
                unitCost: opp.gas_unit_cost
                },
                opp.low_savings_alert,
                opp.project_lead_user_id, 
                opp.organization_name
            );
            setRows(opps);
            break;
            }
        }
    }

    const deleteOpportunity = async () => {
        try{
        let response = await axiosProtect.delete(`/opp/opportunities/delete-opp?opp_id=${oppToDelete.id}`);
        if(response.status === 200){
            let opps = [];

            for(let i = 0;i<rows.length;++i){
                if(rows[i].id == oppToDelete.id) continue;
                opps.push(rows[i]);
            }
            setRows(opps);
        }
        }catch(error){}
        setDeleteModalOpen(false);
        setOppToDelete(null);
    }

    const filteredUsers = useMemo(() => {
        const q = queryString.trim().toLowerCase();

        return users.filter(user => {
            const matchesSearch =
                !q ||
                (user.first_name ?? '').toLowerCase().includes(q) ||
                (user.last_name ?? '').toLowerCase().includes(q) ||
                (user.email ?? '').toLowerCase().includes(q);

            const matchesOrg =
                !selectedOrganization || user.org === selectedOrganization;

            const matchesAccess =
                !selectedAccess || user.role === selectedAccess;

            return matchesSearch && matchesOrg && matchesAccess;
        });
    }, [users, queryString, selectedOrganization, selectedAccess]);

    const organizationOptions = useMemo(() => {
        const base = selectedAccess
            ? users.filter(u => u.role === selectedAccess)
            : users;

        return [...new Set(base.map(u => u.org).filter(Boolean))];
    }, [users, selectedAccess]);

    const accessOptions = useMemo(() => {
        const base = selectedOrganization
            ? users.filter(u => u.org === selectedOrganization)
            : users;

        return [...new Set(base.map(u => u.role).filter(Boolean))];
    }, [users, selectedOrganization]);

    const displayedUsers = useMemo(() => {
        const mult = dir === 'desc' ? -1 : 1;

        const safe = v => (v ?? '').toString().toLowerCase();

        return [...filteredUsers].sort((a, b) => {
            let av = '';
            let bv = '';

            switch (field) {
            case 'name':
                av = safe(`${a.first_name} ${a.last_name}`);
                bv = safe(`${b.first_name} ${b.last_name}`);
                break;

            case 'email':
                av = safe(a.email);
                bv = safe(b.email);
                break;

            case 'organization':
                av = safe(a.org);
                bv = safe(b.org);
                break;

            case 'site':
                av = safe(Array.isArray(a.sites) ? a.sites.join(', ') : a.sites);
                bv = safe(Array.isArray(b.sites) ? b.sites.join(', ') : b.sites);
                break;

            case 'agreed':
                av = a.agreed_to_tos ? 1 : 0;
                bv = b.agreed_to_tos ? 1 : 0;
                break;

            default:
                return 0;
            }

            return av > bv ? mult : av < bv ? -mult : 0;
        });
        }, [filteredUsers, sortState]); 

    return (
        <div style={{padding:'2%', minHeight:'95vh'}}>
            <AccountManagementModal 
                modalState={modalState} setModalState={setModalState} closeModal={closeModal} 
                auth={auth} userToEdit={userToEdit} userToDelete={userToDelete}
                setUserToEdit={setUserToEdit} setUserToDelete={setUserToDelete} 
                users={users} setUsers={setUsers}
            />

            {createModalState && <CreateOppModal createModalState={createModalState} setCreateModalState={setCreateModalState} addOpportunity={addOpportunity} oppToEdit={oppToEdit} updateOpportunity={updateOpportunity} siteID={props.siteID}/>}
            {deleteModalOpen && <DeleteOppModal deleteModalOpen={deleteModalOpen} setDeleteModalOpen={setDeleteModalOpen} oppToDelete={oppToDelete} deleteOpportunity={deleteOpportunity}/>}
            {statusModalOpen && <StatusModal statusModalOpen={statusModalOpen} setStatusModalOpen={setStatusModalOpen}/>}
            {paybackModalOpen && <PaybackModal paybackModalOpen={paybackModalOpen} setPaybackModalOpen={setPaybackModalOpen}/>}
            {/* { mobile ? (
                    <div>
                    {filteredRows.map((row, i) => (
                        <OppCard key={row.id} opp={row} />
                    ))}
                    </div>
                ) : ( */}

            <TableContainer component={Paper}>
                <Table aria-label="collapsible table">
                    <TableHead style={{backgroundColor:'rgb(3,117,176)'}}>
                        <TableRow>
                            <TableCell colSpan={7} style={{color:'whitesmoke', padding:'0.9rem 0.75rem'}}>
                                <div style={{display:'flex', flexDirection:'row'}}>
                                    <h4 style={{color:'whitesmoke', margin:'0', flex:'3'}}>Client accounts and access</h4>
                                    <TextField
                                      sx={{flex: 2,
                                            "& .MuiInputLabel-root": { color: "white" },
                                            "& .MuiInputLabel-root.Mui-focused": { color: "white" },
                                            "& .MuiOutlinedInput-root": {color: "white",
                                                "& fieldset": { borderColor: "white" },
                                                "&:hover fieldset": { borderColor: "white" },
                                                "&.Mui-focused fieldset": { borderColor: "white" }},
                                            "& .MuiInputBase-input::placeholder": { color: "white", opacity: 1 }}}
                                        label="Search"
                                        fullWidth
                                        value={queryString}
                                        onChange={e => setQueryString(e.target.value)}
                                        inputProps={{autoComplete: 'off'}}
                                    />
                                </div>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell style={{color:'whitesmoke'}}>
                            {(auth.role === process.env.REACT_APP_ES_ADMIN || auth.role === process.env.REACT_APP_CLIENT_ADMIN) && 
                                <>
                                    <IconButton onClick={() => setModalState('create')}>
                                        <AddCircleIcon sx={{ color: 'white' }} />
                                    </IconButton>
                                    <Tooltip
                                        onClose={() => setOpenSortTooltip(false)}
                                        open={openSortTooltip}
                                        disableHoverListener
                                        disableFocusListener
                                        disableTouchListener
                                        disableInteractive
                                        PopperProps={{disablePortal: true, style: {zIndex: 1200,} }}
                                        title={
                                            <div style={{ pointerEvents: 'auto' }}>
                                            <React.Fragment>
                                                {
                                                    auth.role === process.env.REACT_APP_ES_ADMIN && 
                                                    <>
                                                    <Typography color="inherit">
                                                        Filter by Organization
                                                    </Typography>
                                                    <FormControl fullWidth>
                                                    <Select
                                                        value={selectedOrganization}
                                                        onChange={(e) => setSelectedOrganization(e.target.value)}
                                                        MenuProps={{
                                                        disablePortal: false,
                                                        disableAutoFocusItem: true,
                                                        disableEnforceFocus: true,
                                                        disableScrollLock: true,
                                                        PaperProps: {style: {zIndex: 2000,}}}}
                                                        sx={{height: '3rem',color: 'white',
                                                        '& .MuiOutlinedInput-notchedOutline': {borderColor: 'white'},
                                                        '& .MuiSvgIcon-root': {color: 'white'}}}
                                                    >
                                                        {organizationOptions.map((organization) => (
                                                            organization ? (
                                                                <MenuItem key={organization} value={organization}>
                                                                {organization}
                                                                </MenuItem>
                                                            ) : null
                                                            ))}
                                                    </Select>
                                                    </FormControl>
                                                    </>
                                                }
                                                <Typography color="inherit" style={{ marginTop: 12 }}>
                                                    Filter by Account Type
                                                </Typography>
                                                <FormControl fullWidth>
                                                    <Select
                                                        value={selectedAccess}
                                                        onChange={(e) => setSelectedAccess(e.target.value)}
                                                        MenuProps={{
                                                            disablePortal: false,
                                                            disableAutoFocusItem: true,
                                                            disableEnforceFocus: true,
                                                            disableScrollLock: true,
                                                            PaperProps: {style: { zIndex: 2000}}}}
                                                        sx={{
                                                            height: '3rem',
                                                            color: 'white',
                                                            '& .MuiOutlinedInput-notchedOutline': {borderColor: 'white'},
                                                            '& .MuiSvgIcon-root': {color: 'white'}}}
                                                    >
                                                        <MenuItem value="">
                                                            All Access
                                                        </MenuItem>
                                                        {accessOptions.map((role) => (
                                                        role ? (
                                                            <MenuItem key={role} value={role}>
                                                            {role}
                                                            </MenuItem>
                                                        ) : null
                                                        ))}   
                                                    </Select>
                                                </FormControl>
                                                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%",}}>
                                                    <IconButton style={{ fontSize: "1rem", color: "whitesmoke" }}
                                                        onClick={() => setOpenSortTooltip(false)}
                                                    >
                                                        Close
                                                    </IconButton>
                                                    <IconButton style={{ fontSize: "1rem", color: "whitesmoke" }}
                                                        onClick={() => {
                                                            setSelectedAccess('');
                                                            setSelectedOrganization('');
                                                    }}
                                                    >
                                                        Reset
                                                    </IconButton>
                                                    </div>
                                            </React.Fragment>
                                            </div>
                                        }
                                        arrow
                                        placement="right"
                                        >
                                        <IconButton onClick={() => setOpenSortTooltip(true)}>
                                            <FilterListIcon style={{ color: 'whitesmoke' }} />
                                        </IconButton>
                                        </Tooltip>
                                </>
                            }
                            </TableCell>
                            {auth.role === process.env.REACT_APP_ES_ADMIN && 
                                <TableCell style={{color:'whitesmoke'}}>
                                    <div style={{display:'flex', flexDirection:'row'}}>
                                        <span style={{width:'fit-content'}}>Organization</span>
                                        <div style={{width:'3%'}}></div>
                                        <div style={{flex:'1', textAlign:'left'}}>
                                            <SortIcon 
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    setSortState(prev => ({
                                                        field: 'organization',
                                                        direction: prev.field === 'organization' && prev.direction === 'asc'? 'desc' : 'asc' }
                                                    ))
                                                }
                                            />
                                        </div>
                                    </div>
                                </TableCell>
                            }   
                            <TableCell style={{color:'whitesmoke'}}>
                                <div style={{display:'flex', flexDirection:'row'}}>
                                    <span style={{width:'fit-content'}}>Name</span>
                                    <div style={{width:'3%'}}></div>
                                    <div style={{flex:'1', textAlign:'left'}}>
                                        <SortIcon 
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                setSortState(prev => ({
                                                    field: 'name',
                                                    direction: prev.field === 'name' && prev.direction === 'asc'? 'desc' : 'asc' }
                                                ))
                                            }
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell style={{color:'whitesmoke'}}>
                                <div style={{display:'flex', flexDirection:'row'}}>
                                    <span style={{width:'fit-content'}}>Email</span>
                                    <div style={{width:'3%'}}></div>
                                    <div style={{flex:'1', textAlign:'left'}}>
                                        <SortIcon 
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                setSortState(prev => ({
                                                    field: 'email',
                                                    direction: prev.field === 'email' && prev.direction === 'asc'? 'desc' : 'asc' }
                                                ))
                                            }
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell align="left" style={{color:'whitesmoke'}}>
                                <div style={{display:'flex', flexDirection:'row'}}>
                                    <span style={{width:'fit-content'}}>Site access</span>
                                    <div style={{width:'3%'}}></div>
                                    <div style={{flex:'1', textAlign:'left'}}>
                                        <SortIcon 
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                setSortState(prev => ({
                                                    field: 'site',
                                                    direction: prev.field === 'site' && prev.direction === 'asc'? 'desc' : 'asc' }
                                                ))
                                            }
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            {auth.role === process.env.REACT_APP_ES_ADMIN &&
                            <TableCell style={{color:'whitesmoke'}}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    <span style={{ whiteSpace: 'nowrap' }}>Agreed to TOS</span>                                
                                    <div style={{ flex: '1', textAlign: 'left' }}>
                                    <SortIcon
                                        style={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            setSortState(prev => ({
                                                field: 'agreed',
                                                direction: prev.field === 'agreed' && prev.direction === 'asc'? 'desc' : 'asc' }
                                            ))
                                        }
                                    />
                                    </div>                                   
                                </div>
                            </TableCell>
                            }
                            <TableCell align="right" style={{color:'whitesmoke'}}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedUsers.map((user) => (
                            <TableRow
                            key={user._id}
                            sx={{ '& > *': { borderBottom: 'unset' } }}
                            >
                            <TableCell>{user.role}</TableCell>
                            {auth.role === process.env.REACT_APP_ES_ADMIN && (
                                <TableCell>{user.org}</TableCell>
                            )}
                            <TableCell>
                                {user.first_name} {user.last_name}
                                {user._id === auth.id ? ' (you)' : ''}
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                                {Array.isArray(user.sites) ? (
                                <>
                                    {user.sites.map((site, i) => (
                                    <React.Fragment key={`${site}-${i}`}>
                                        <span>{site}</span>
                                        {i !== user.sites.length - 1 && <br />}
                                    </React.Fragment>
                                    ))}
                                </>
                                ) : (
                                user.sites
                                )}
                            </TableCell>
                            {auth.role === process.env.REACT_APP_ES_ADMIN && (
                                <TableCell align="center">
                                {user.agreed_to_tos ? 'Yes' : 'No'}
                                </TableCell>
                            )}
                            <TableCell align="right"> {(
                                [process.env.REACT_APP_ES_ADMIN, process.env.REACT_APP_CLIENT_ADMIN].includes(auth.role) &&
                                (auth.role === process.env.REACT_APP_ES_ADMIN || user.role === 'Staff user') &&
                                !user.role.startsWith('Exception') &&
                                auth.id !== user._id
                                ) && (
                                <>
                                    <IconButton onClick={() => handleEditClick(user)}>
                                    <EditTwoToneIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDeleteClick(user)}>
                                    <DeleteTwoToneIcon style={{ fill: 'red' }} />
                                    </IconButton>
                                </>
                                )}
                            </TableCell>
                            </TableRow>
                        ))}
                        </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}      

export default UserAccountPage;